import { HelperService } from 'src/app/core/service/helper.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { sortBy, reject } from 'lodash';
import { TeamEdit, TeamRole } from '../model/team';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { User } from 'src/app/user/model/user';
import { TeamService } from 'src/app/core/service/team.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Code, Dictionary } from 'src/app/core/model/dictionary';
import { RegistrationService } from 'src/app/core/service/registration.service';
import { UserService } from 'src/app/core/service/user.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { DirectorySearchComponent } from 'src/app/shared/directory-search/directory-search.component';
import { Organization } from 'src/app/core/model/organization';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import moment from 'moment';
import { AddressValidatorComponent } from 'src/app/shared/address-validator/address-validator.component';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'app-team-edit-non-user',
  templateUrl: './team-edit-non-user.component.html',
  styleUrls: ['./team-edit-non-user.component.scss']
})
export class TeamEditNonUserComponent implements OnInit
{
  @ViewChild('addressValidator') public addressValidator: AddressValidatorComponent;
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild('directorySearch') public directorySearch: DirectorySearchComponent;

  @Input() public isNew = false;
  @Input() public memberId: number;
  @Input() public caseId: number;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<TeamEdit> = new EventEmitter();
  @Output() public delete: EventEmitter<TeamEdit> = new EventEmitter();

  public urlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;
  public viewExtensions: GridViewManager;
  public editForm: any;
  public editFormActive = false;
  public displaySubRoles = false;
  public emailRequired = false;
  public users: User[] = [];
  public teamRoles: TeamRole[] = [];
  public subRoles: Code[] = [];
  public confirmDeleteDialogOpened = false;
  public roles: Array<{ text: string; value: string }>;
  public patientLabel = {};
  public states: Code[] = [];
  public countries: Code[] = [];
  public genders: Code[] = [];
  public notifications: Array<{ text: string; value: string }> = [];
  public types: any[] = [];
  public sources: any[] = [];
  public identifiersVisible = false;
  public providerEnabled = false;
  public kno2Enabled = false;
  public careTeamAccountRegistrationEnabled = true;
  public spokenLanguagesFiltered: Dictionary[] = [];
  public allSpokenLanguages = [];
  public virtualSpokenLanguage: any = { itemHeight: 30 };
  public accountActivated = false;
  public addressChanged = false;
  public saving = false;
  public careTeamToSave: any;
  public addressAPIEnabled = false;
  public controlsDisabled = false;
  public accessProviders = false;
  public isOperatorRole = false;
  public extensionOrganizations = [];
  public extensionsByCategory = [];
  public extensionsByCategoryAndOrganization = [];
  public extensionsByCategoryAndOrganizationUnused = [];
  public ctExtensionsEditable = false;

  /**
   * FormGroup for in-grid editing of care team extension.
   */
  public ctExtensionFormGroup: any = null;

  /**
   * Maximum number of items for virtualization. This is to accelerate the DOM performance.
   */
  public virtualSubRole: any = {
    itemHeight: 30
  };

  /**
   * Phone number input format mask
   */
  public phoneNumberMask: string;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Filtered list of team roles for drop-down.
   */
  public teamRolesFiltered: TeamRole[] = [];

  /**
   * List of available organizations
   */
  public organizations: Organization[];

  /**
   * Coordinator service admin role?
   */
  public isCommunityCoordinatorRole = false;

  public currentUserOrganization: Organization;

  /**
   * Filtered list of organizations for drop-down.
   */
  public organizationsFiltered: Organization[] = [];

  private lastSelectedProviderSpecialtyCode: any = null;
  private teamNonUserEdit: TeamEdit;

  /**
   * Current logged in user
   */
  private currentUser: User;

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService,
    private teamService: TeamService,
    private registrationService: RegistrationService,
    private userService: UserService,
    private helperService: HelperService,
    private authenticationService: AuthenticationService,
    private organizationService: OrganizationService,
    private settingService: SettingService,
    private extensionService: ExtensionService)
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.providerEnabled = sessionSetting.providerEnabled;
        this.kno2Enabled = sessionSetting.kno2Enabled;
        this.careTeamAccountRegistrationEnabled = sessionSetting.careTeamAccountRegistrationEnabled;
        this.addressAPIEnabled = sessionSetting.addressAPIEnabled;
        this.accessProviders = (sessionSetting.providersRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.providersRoles) : false;
        this.ctExtensionsEditable = (sessionSetting.addUpdateExtensionsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addUpdateExtensionsRoles) : false;
      }
    });
    this.isOperatorRole = this.authenticationService.currentUser.role === 'ROLE_OPERATOR';
    this.extensionService.getDistinctExtensionOrganizations('CARE_TEAM').subscribe({
      next: (data) =>
      {
        this.extensionOrganizations = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
    this.viewExtensions = new GridViewManager(99999);
    this.viewExtensions.setData([]);
  }

  public get extensions(): UntypedFormControl
  {
    return this.editForm.get('extensions');
  }

  public get longitude(): UntypedFormControl
  {
    return this.editForm.get('longitude');
  }

  public get latitude(): UntypedFormControl
  {
    return this.editForm.get('latitude');
  }

  public get subRole(): UntypedFormControl
  {
    return this.editForm.get('subRole');
  }

  public get identifiers(): UntypedFormControl
  {
    return this.editForm.get('identifiers');
  }

  public get email(): UntypedFormControl
  {
    return this.editForm.get('email');
  }

  public get createAccount(): UntypedFormControl
  {
    return this.editForm.get('createAccount');
  }

  public get accountRole(): UntypedFormControl
  {
    return this.editForm.get('accountRole');
  }

  public get accountOrganization(): UntypedFormControl
  {
    return this.editForm.get('accountOrganization');
  }

  public get lastName(): UntypedFormControl
  {
    return this.editForm.get('lastName');
  }

  public get firstName(): UntypedFormControl
  {
    return this.editForm.get('firstName');
  }

  public get url(): UntypedFormControl
  {
    return this.editForm.get('url');
  }

  public get middleName(): UntypedFormControl
  {
    return this.editForm.get('middleName');
  }

  public get title(): UntypedFormControl
  {
    return this.editForm.get('title');
  }

  public get suffix(): UntypedFormControl
  {
    return this.editForm.get('suffix');
  }

  public get professionalSuffix(): UntypedFormControl
  {
    return this.editForm.get('professionalSuffix');
  }

  public get spokenLanguages(): UntypedFormControl
  {
    return this.editForm.get('spokenLanguages');
  }

  public get homePhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('homePhoneNumber');
  }

  public get homePhoneNumberExtension(): UntypedFormControl
  {
    return this.editForm.get('homePhoneNumberExtension');
  }

  public get cellPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('cellPhoneNumber');
  }

  public get otherPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('otherPhoneNumber');
  }

  public get workPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('workPhoneNumber');
  }

  public get workPhoneNumberExtension(): UntypedFormControl
  {
    return this.editForm.get('workPhoneNumberExtension');
  }

  public get faxNumber(): UntypedFormControl
  {
    return this.editForm.get('faxNumber');
  }

  public get taxNo(): UntypedFormControl
  {
    return this.editForm.get('taxNo');
  }

  public get organization(): UntypedFormControl
  {
    return this.editForm.get('organization');
  }

  public get npi(): UntypedFormControl
  {
    return this.editForm.get('npi');
  }

  public get providerDirectorySource(): UntypedFormControl
  {
    return this.editForm.get('providerDirectorySource');
  }

  public get directAddress(): UntypedFormControl
  {
    return this.editForm.get('directAddress');
  }

  public get streetLine(): UntypedFormControl
  {
    return this.editForm.get('streetLine');
  }

  public get accountCreated(): UntypedFormControl
  {
    return this.editForm.get('accountCreated');
  }

  public get streetLineAdditional(): UntypedFormControl
  {
    return this.editForm.get('streetLineAdditional');
  }

  public get city(): UntypedFormControl
  {
    return this.editForm.get('city');
  }

  public get county(): UntypedFormControl
  {
    return this.editForm.get('county');
  }

  public get postalCode(): UntypedFormControl
  {
    return this.editForm.get('postalCode');
  }

  public get state(): UntypedFormControl
  {
    return this.editForm.get('state');
  }

  public get country(): UntypedFormControl
  {
    return this.editForm.get('country');
  }

  public get gender(): UntypedFormControl
  {
    return this.editForm.get('gender');
  }

  public get notification(): UntypedFormControl
  {
    return this.editForm.get('notification');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get from(): UntypedFormControl
  {
    return this.editForm.get('from');
  }

  public get to(): UntypedFormControl
  {
    return this.editForm.get('to');
  }

  @Input() public set model(teamNonUserEdit: TeamEdit)
  {
    this.initModel(teamNonUserEdit);
  }

  public ngOnInit(): void
  {
    // Roles with restrictions on Care Coordinator only
    this.roles = this.userService.roles.filter(r => r.value === 'ROLE_CARE_COORDINATOR');

    this.notifications = this.teamService.notifications;

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.dictionaryService.getCachedLanguages().subscribe({
      next: (data) =>
      {
        this.allSpokenLanguages = data;
        this.filterSpokenLanguages('');
      }
    });

    this.currentUser = this.authenticationService.currentUser;
    if (this.currentUser)
    {
      this.userService.getUserById(this.currentUser.id, false).subscribe({
        next: (user) =>
        {
          this.isCommunityCoordinatorRole = user.role === 'ROLE_COMMUNITY_COORDINATOR';
          this.currentUserOrganization = user.organization;
        }
      });
    }

    // Get Organizations
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        this.filterOrganizations('');
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });

    // get list of states/provinces from Dictionary table
    this.helperService.getGendersStatesCountriesFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.genders = responseList[0];
        this.states = responseList[1];
        this.countries = responseList[2];
      }
    });

    this.dictionaryService.getCachedIdentifiers().subscribe({
      next: (types) =>
      {
        this.types = types;
      }
    });

    this.dictionaryService.getCachedIdentifierSources().subscribe({
      next: (sources) =>
      {
        this.sources = sources;
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    if ((this.firstName.value === '' && this.lastName.value === '' && this.organization.value === '') ||
      ((this.firstName.value !== '' && this.lastName.value === '') || (this.firstName.value === '' && this.lastName.value !== '')))
    {
      this.messageHandlerService.displayError('REQUIRED-PROVIDER-ORGANIZATION-OR-NAME');
      return;
    }

    // Reset account to null if not used
    if (!this.createAccount.value)
    {
      this.accountRole.setValue(null);
      this.accountOrganization.setValue(null);
    }

    if (this.createAccount.value && !this.email.value && this.providerDirectorySource.value === 'CHANNELS')
    {
      this.messageHandlerService.displayError('MISSING-EMAIL-ON-PROVIDER');
      return;
    }

    const objectToPersist = this.editForm.value;

    objectToPersist.longitude = this.longitude.value;
    objectToPersist.latitude = this.latitude.value;

    objectToPersist.identifiers = this.identifiers.value;
    this.setIdentifiers(objectToPersist);

    objectToPersist.extensions = this.viewExtensions.gridData.data;

    // Must remove temporary guid from extensions
    objectToPersist.extensions.forEach((e) => { if (!Number.isFinite(e.id)) { e.id = null; } });

    if (this.state.value === undefined)
    {
      objectToPersist.state = '';
    }

    if (this.country.value === undefined)
    {
      objectToPersist.country = '';
    }

    if (this.gender.value === undefined)
    {
      objectToPersist.gender = '';
    }

    // We must set manually because for Channels Providers controls are disabled but we still need to send to back-end
    objectToPersist.providerDirectorySource = this.providerDirectorySource.value;
    objectToPersist.npi = this.npi.value;
    objectToPersist.lastName = this.lastName.value;
    objectToPersist.firstName = this.firstName.value;
    objectToPersist.organization = this.organization.value;
    objectToPersist.gender = this.gender.value;
    objectToPersist.spokenLanguages = this.spokenLanguages.value;

    this.setAddress(objectToPersist);

    // Check if account is already created when create account is enable.
    // If account exists but not confirmed yet the create account is disabled and therefore we don't need to validate.
    // This allow updating other properties without re-sending registration (unless email is changed).
    if (this.createAccount.value && this.email.value &&
      !this.teamNonUserEdit.accountCreated && this.createAccount.enabled)
    {
      // First/Last name are required if creating an account
      if (this.firstName.value === '' && this.lastName.value === '')
      {
        this.messageHandlerService.displayError('REQUIRED-FIRST-AND-LAST-NAME');
        return;
      }
      this.registrationService.emailExists(this.email.value).subscribe({
        next: (response) =>
        {
          if (response)
          {
            this.messageHandlerService.displayError('EMAIL-ALREADY-REGISTERED-ACCOUNT');
          }
          else
          {
            this.performCareTeamSave(objectToPersist);
          }
        }
      });
    }
    else
    {
      this.performCareTeamSave(objectToPersist);
    }
  }

  public onDelete(e: Event): void
  {
    e.preventDefault();

    this.delete.emit(this.teamNonUserEdit);
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Providers have been selected from the Kno2 dialog.
   * Set the form properties witht the selected provider.
   *
   * @param provider Select provider list (only one selection allowed) selected from the dialog.
   */
  public onSelectProviders(provider: any): void
  {
    this.editForm.patchValue(
      {
        firstName: provider[0].firstName !== undefined ? provider[0].firstName : '',
        lastName: provider[0].lastName !== undefined ? provider[0].lastName : '',
        middleName: provider[0].middleName !== undefined ? provider[0].middleName : '',
        suffix: provider[0].suffix !== undefined ? provider[0].suffix : '',
        professionalSuffix: provider[0].professionalSuffix !== undefined ? provider[0].professionalSuffix : '',
        spokenLanguages: provider[0].languages !== undefined ? provider[0].languages : null,
        gender: provider[0].gender !== undefined ? provider[0].gender : '',
        homePhoneNumber: provider[0].telephoneNumber !== undefined ? provider[0].telephoneNumber : '',
        homePhoneNumberExtension: provider[0].telephoneNumberExtension !== undefined ? provider[0].telephoneNumberExtension : '',
        cellPhoneNumber: provider[0].cellPhoneNumber !== undefined ? provider[0].cellPhoneNumber : '',
        otherPhoneNumber: null,               // Not supported in Provider yet
        workPhoneNumber: null,                // Not supported in Provider yet
        workPhoneNumberExtension: null,       // Not supported in Provider yet
        faxNumber: provider[0].faxNumber !== undefined ? provider[0].faxNumber : '',
        organization: provider[0].organization !== undefined ? provider[0].organization : '',
        email: provider[0].email !== undefined ? provider[0].email : '',
        title: provider[0].title !== undefined ? provider[0].title : '',
        directAddress: provider[0].directAddress !== undefined ? provider[0].directAddress : '',
        npi: provider[0].npi !== undefined ? provider[0].npi : '',
        providerDirectorySource: provider[0].directorySource,
        streetLine: provider[0].practiceAddress ? provider[0].practiceAddress.streetLine : null,
        streetLineAdditional: provider[0].practiceAddress ? provider[0].practiceAddress.streetLineAdditional : null,
        city: provider[0].practiceAddress ? provider[0].practiceAddress.city : null,
        county: provider[0].practiceAddress ? provider[0].practiceAddress.county : null,
        postalCode: provider[0].practiceAddress ? provider[0].practiceAddress.postalCode : null,
        state: provider[0].practiceAddress ? provider[0].practiceAddress.state : null,
        country: provider[0].practiceAddress ? provider[0].practiceAddress.country : null,
        url: provider[0].url !== undefined ? provider[0].url : '',
        description: provider[0].description !== undefined ? provider[0].description : '',
      }, { emitEvent: false });

    this.setControlsState();
    this.editForm.markAsDirty();

    if (this.providerDirectorySource.value === 'CHANNELS' && this.editForm !== undefined)
    {
      (this.editForm.controls.identifiers as UntypedFormArray).clear();
    }

    // Keep the Channels provider specialy in case of being used
    if (this.providerDirectorySource.value === 'CHANNELS' && provider[0].specialty)
    {
      this.lastSelectedProviderSpecialtyCode = provider[0].specialty;
    }
    else
    {
      this.lastSelectedProviderSpecialtyCode = null;
    }
  }

  /**
   * Open the Kno2 Search Directory dialog.
   */
  public searchProviders(): void
  {
    this.tabstrip.selectTab(0);
    this.directorySearch.execute();
  }

  public getDialogWidth(): any
  {
    return '1025px';
  }

  /**
   * Filters the team roles on user input.
   */
  public filterTeamRoles(filter: string): void
  {
    this.searchTerm = filter;
    this.teamRolesFiltered = this.teamRoles
      .filter(user => user.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Check if team role from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Filter the organizations based on user input.
   */
  public filterOrganizations(filterValue: string): void
  {
    this.organizationsFiltered = sortBy(this.organizations
      .filter(organization => organization.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  /**
   * Add a Care Team identifier.
   *
   * @param id Identifier id
   * @param version Identifier record version
   * @param type Identifier type (dictionary)
   * @param source Identifier source (dictionary)
   * @param value Identifier value
   * @param isLoading Indicate if this is loading for the first time. This would prevent markAsDirty if true.
   */
  public addIdentifier(id: number = null, version = 0, type: any = null, source: any = null,
    value: string = null, isLoading = false): void
  {
    this.editForm.controls.identifiers.push(this.initIdentifier(id, version, type, source, value));
    if (!isLoading)
    {
      this.editForm.markAsDirty();
    }
  }

  /**
   * Remove an existing identifier entry.
   *
   * @param i Index of the record in the list.
   * @param id Identifier id if never saved.
   */
  public removeIdentifier(i: number, id: number): void
  {
    const control = this.editForm.controls.identifiers;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      const rejectedId: any = {};
      rejectedId.id = id;
      this.editForm.identifiers = reject(this.editForm.identifiers, rejectedId);
    }
    control.removeAt(i);
    this.editForm.touched = true;
    this.editForm.markAsDirty();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.viewExtensions.setState(state);
  }

  public isValidUrl(): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return this.url.value !== undefined && this.url.value && regEx.test(this.url.value);
  }

  public openUrl(): void
  {
    if (this.isValidUrl())
    {
      window.open(this.url.value);
    }
  }

  /**
   * Filters the Spoken Languages based on user input.
   */
  public filterSpokenLanguages(filter: string): void
  {
    this.searchTerm = filter;
    this.spokenLanguagesFiltered = this.allSpokenLanguages
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Address change event from Address Validator component.
   *
   * @param event Address data being selected to apply.
   */
  public changeAddress(event: any): void
  {
    // Override the address based on Address API response.
    if (event.confirmation === 'recommended')
    {
      // Force dirty to change state
      this.editForm.markAsDirty();

      // Set form properties
      this.streetLine.setValue(event.line1);
      this.streetLineAdditional.setValue(event.line2);
      const state = event.postalAddress && event.postalAddress.administrativeArea ?
        this.states.find(x => x.code === event.postalAddress.administrativeArea) : null;
      this.state.setValue(state !== null ? state.code : null);
      this.city.setValue(event.postalAddress && event.postalAddress.locality ? event.postalAddress.locality : null);
      this.postalCode.setValue(event.postalAddress && event.postalAddress.postalCode ? event.postalAddress.postalCode : null);
      this.latitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.latitude : null);
      this.longitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.longitude : null);

      if (event.postalAddress && event.postalAddress.regionCode)
      {
        const country = this.countries.find(x => x.alternateCode === event.postalAddress.regionCode);
        this.country.setValue(country ? country.code : null);
      }
      this.addressChanged = false;    // Mark address unchanged to avoid opening modal again unless it is manually changed again.
    }
    else
    {
      this.latitude.setValue(null);
      this.longitude.setValue(null);
    }

    // If autoSave, then set patient object and save.
    if (event.autoSave)
    {
      this.setAddress(this.careTeamToSave);
      this.saveCareTeam();
    }
  }

  /**
   * Manual Address Validation. No autoSave will be performed.
   */
  public validateAddress(): void
  {
    this.addressValidator.execute(false, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
      this.state.value, this.country.value, this.postalCode.value);
  }

  public saveCareTeam(): void
  {
    if (this.from.value !== null && this.to.value !== null)
    {
      const d1 = moment(this.to.value);
      const d2 = moment(this.from.value);
      const diffMins = d1.diff(d2, 'minutes');
      if (diffMins <= 0)
      {
        this.messageHandlerService.displayError('ACTIVE-DATE-RANGE-ERROR-FROM-TO');
        return;
      }
    }
    this.save.emit(this.careTeamToSave);
    this.addressChanged = false;
    this.saving = false;
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  /**
   * Handles the add new row event of the Kendo grid for care team extension.
   * Adds a new row to the grid, and creates a FormGroup for data input.
   */
  public ctExtensionAddHandler(event: GridEventHandler): void
  {
    // Get the default organization
    let defaultOrganization: Organization;
    if (this.currentUserOrganization !== undefined)
    {
      defaultOrganization = this.organizations.find(x => x.id === this.currentUserOrganization.id);
    }

    this.ctExtensionFormGroup = this.fb.group(
      {
        isNew: true,
        id: Guid.create().toString(),
        organization: [{
          value: defaultOrganization,
          disabled: false
        }, Validators.required],
        settingExtension: [{ value: null, disabled: false }, Validators.required],
        value: [{ value: null, disabled: false }, Validators.required],
        editing: true
      }
    );

    event.sender.addRow(this.ctExtensionFormGroup);

    if (defaultOrganization !== undefined)
    {
      this.organizationChange(false);
    }
  }

  /**
   * Extension organization changed.
   */
  public organizationChange(reset: boolean): void
  {
    if (reset)
    {
      this.extensionsByCategory = [];
      this.extensionsByCategoryAndOrganization = [];
      this.extensionsByCategoryAndOrganizationUnused = [];
      this.ctExtensionFormGroup.get('settingExtension').setValue(null);
    }
    if (this.ctExtensionFormGroup.get('organization').value)
    {
      this.extensionService.getExtensionsByCategory('CARE_TEAM').subscribe({
        next: (data) =>
        {
          this.extensionsByCategory = data;
          this.extensionsByCategoryAndOrganization = this.extensionsByCategory.filter(
            extension => extension.organization === null || extension.organization.id === this.ctExtensionFormGroup.get('organization').value.id);
          const extensionsInUse = this.viewExtensions.gridData.data.filter(extension =>
            extension.id !== (this.ctExtensionFormGroup.get('organization').value ? this.ctExtensionFormGroup.get('organization').value.id : null));
          this.extensionsByCategoryAndOrganizationUnused = sortBy(
            this.extensionsByCategoryAndOrganization.filter(u => !extensionsInUse.map(m => m.settingExtension.id).includes(u.id)), ['name']);
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Care Team Extension save action.
   *
   * @param event Current record
   */
  public ctExtensionSaveHandler(event: GridEventHandler): void
  {
    this.ctExtensionFormGroup.get('editing').setValue(false);

    // Construct data
    const data: any = {};
    data.id = this.ctExtensionFormGroup.get('id').value;
    data.settingExtension =
      this.extensionsByCategoryAndOrganization.find(x => x.name === this.ctExtensionFormGroup.get('settingExtension').value.name);
    data.value = this.ctExtensionFormGroup.get('value').value;
    data.organizationId = this.ctExtensionFormGroup.get('organization').value.id;
    data.settingExtension.organization = this.ctExtensionFormGroup.get('organization').value;

    if (this.ctExtensionFormGroup.get('isNew').value) 
    {
      this.extensions.value.push(data);
    }
    else
    {
      const currentRowIndex = this.extensions.value.findIndex(x => x.id === data.id);
      if (currentRowIndex >= 0)
      {
        this.extensions.value[currentRowIndex] = data;
      }
    }

    this.viewExtensions.setData(this.extensions.value);

    event.sender.closeRow(event.rowIndex);

    this.editForm.markAsDirty();
  }

  /**
   * Handles the edit event of the patient extensions grid.
   */
  public ctExtensionEditHandler(event: GridEventHandler): void
  {
    this.ctExtensionFormGroup = this.fb.group(
      {
        isNew: false,
        id: event.dataItem.id,
        organization: [event.dataItem.settingExtension.organization, Validators.required],
        value: [event.dataItem.value, Validators.required],
        editing: true,
        settingExtension: [event.dataItem.settingExtension, Validators.required]
      }
    );
    this.organizationChange(false);
    event.sender.editRow(event.rowIndex, this.ctExtensionFormGroup);
  }

  /**
   * Cancelling the care team extension add action.
   *
   * @param event Current row
   */
  public ctExtensionCancelHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Delete entered care team extension from grid.
   *
   * @param extension Care Team Extension record to remove.
   */
  public deleteCareTeamExtension(extension: any): void
  {
    this.extensions.setValue(this.extensions.value.filter(c => c.id !== extension.id));
    this.viewExtensions.setData(this.extensions.value);
    this.editForm.markAsDirty();
  }

  public isOrganizationHasExtension(id: number): boolean 
  {
    return this.extensionOrganizations.map(x => x.id).includes(id);
  }

  private setAddress(objectToPersist: any): void
  {
    objectToPersist.streetLine = this.streetLine && this.streetLine.value !== '' ? this.streetLine.value : null;
    objectToPersist.streetLineAdditional = this.streetLineAdditional && this.streetLineAdditional.value !== '' ?
      this.streetLineAdditional.value : null;
    objectToPersist.city = this.city && this.city.value !== '' ? this.city.value : null;
    objectToPersist.county = this.county && this.county.value !== '' ? this.county.value : null;
    objectToPersist.postalCode = this.postalCode && this.postalCode.value !== '' ? this.postalCode.value : null;
    objectToPersist.state = this.state && this.state.value !== '' ? this.state.value : null;
    objectToPersist.country = this.country && this.country.value !== '' ? this.country.value : null;
    objectToPersist.longitude = this.longitude && this.longitude.value !== '' ? this.longitude.value : null;
    objectToPersist.latitude = this.longitude && this.latitude.value !== '' ? this.latitude.value : null;
  }

  private performCareTeamSave(careTeam: any): void
  {
    this.saving = true;
    this.careTeamToSave = careTeam;
    if (this.addressChanged && this.streetLine.value && !this.controlsDisabled)
    {
      this.addressValidator.execute(true, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
        this.state.value ? this.state.value.code : null, this.country.value ? this.country.value.code : null, this.postalCode.value);
    }
    else
    {
      this.saveCareTeam();
    }
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    document.body.style.overflow = '';
    this.cancelling.emit();
  }

  private initModel(teamNonUserEdit: TeamEdit)
  {
    if (teamNonUserEdit === undefined) { return; }

    this.accountActivated = false;
    this.emailRequired = false;
    this.displaySubRoles = false;

    if (this.isNew)
    {
      // Set form
      this.editForm = this.fb.group({
        id: [null],
        member: [null],
        case: [null],
        personId: [null],
        firstName: [''],
        lastName: [''],
        middleName: [null],
        suffix: [null],
        professionalSuffix: [null],
        spokenLanguages: [null],
        gender: [null],
        title: [null],
        organization: [''],
        url: [
          { value: '', disabled: false },
          Validators.pattern(this.urlPattern)
        ],
        email: [null],
        homePhoneNumber: [null],
        homePhoneNumberExtension: [null],
        cellPhoneNumber: [null],
        otherPhoneNumber: [null],
        workPhoneNumber: [null],
        workPhoneNumberExtension: [null],
        faxNumber: [null],
        taxNo: [null],
        role: [null, Validators.required],
        subRole: [null],
        createAccount: [{
          value: false,
          disabled: !this.careTeamAccountRegistrationEnabled
        }],
        accountCreated: [false],
        accountRole: [null],
        accountOrganization: [null],
        npi: [null],
        directAddress: [null],
        providerDirectorySource: [null],
        streetLine: [null],
        streetLineAdditional: [null],
        city: [null],
        county: [null],
        postalCode: [null],
        state: [null],
        country: [null],
        notification: ['NO_NOTIFICATION', Validators.required],
        identifiers: this.fb.array([]),
        description: [''],
        from: [null],
        to: [null],
        longitude: null,
        latitude: null,
        extensions: this.fb.array([])
      });

      this.viewExtensions.setData([]);
      this.viewExtensions.setSort([{ field: 'settingExtension.name', dir: 'asc' }]);
    }
    else
    {
      const subRoleValidator = [];

      if ((teamNonUserEdit.role !== undefined && teamNonUserEdit.role !== null && teamNonUserEdit.role.teamType === null) ||
        teamNonUserEdit.role === null)
      {
        this.displaySubRoles = false;
      }
      else
      {
        this.getSubRole(teamNonUserEdit.role.teamType);
        subRoleValidator.push(Validators.required);
      }

      // Set email and account role validators
      const emailValidator = [Validators.email];
      const accountRoleValidator = [];
      const accountOrganizationValidator = [];

      // Make email and user role required if want to create an account.
      if (teamNonUserEdit.createAccount)
      {
        accountRoleValidator.push(Validators.required);
        accountOrganizationValidator.push(Validators.required);
        emailValidator.push(Validators.required);
        this.emailRequired = true;
      }

      this.controlsDisabled = teamNonUserEdit.providerDirectorySource === 'CHANNELS';
      this.identifiersVisible = !this.controlsDisabled;

      this.viewExtensions.setData(teamNonUserEdit.extensions ? teamNonUserEdit.extensions : []);
      this.viewExtensions.setSort([{ field: 'settingExtension.name', dir: 'asc' }]);

      this.editForm = this.fb.group({
        id: [teamNonUserEdit.id],
        member: [teamNonUserEdit.member],
        case: [teamNonUserEdit.case],
        personId: [teamNonUserEdit.personId],
        firstName: [{ value: teamNonUserEdit.firstName, disabled: this.controlsDisabled }],
        lastName: [{ value: teamNonUserEdit.lastName, disabled: this.controlsDisabled }],
        middleName: [{ value: teamNonUserEdit.middleName, disabled: this.controlsDisabled }],
        suffix: [{ value: teamNonUserEdit.suffix, disabled: this.controlsDisabled }],
        professionalSuffix: [{ value: teamNonUserEdit.professionalSuffix, disabled: this.controlsDisabled }],
        spokenLanguages: [{ value: teamNonUserEdit.spokenLanguages, disabled: this.controlsDisabled }],
        gender: [{ value: teamNonUserEdit.gender, disabled: this.controlsDisabled }],
        title: [{ value: teamNonUserEdit.title, disabled: this.controlsDisabled }],
        organization: [{ value: teamNonUserEdit.organization, disabled: this.controlsDisabled }],
        url: [
          { value: teamNonUserEdit.url, disabled: this.controlsDisabled },
          Validators.pattern(this.urlPattern)
        ],
        email: [{ value: teamNonUserEdit.email, disabled: this.controlsDisabled }, emailValidator],
        homePhoneNumber: [{ value: teamNonUserEdit.homePhoneNumber, disabled: this.controlsDisabled }],
        homePhoneNumberExtension: [{ value: teamNonUserEdit.homePhoneNumberExtension, disabled: this.controlsDisabled }],
        cellPhoneNumber: [{ value: teamNonUserEdit.cellPhoneNumber, disabled: this.controlsDisabled }],
        otherPhoneNumber: [{ value: teamNonUserEdit.otherPhoneNumber, disabled: this.controlsDisabled }],
        workPhoneNumber: [{ value: teamNonUserEdit.workPhoneNumber, disabled: this.controlsDisabled }],
        workPhoneNumberExtension: [{ value: teamNonUserEdit.workPhoneNumberExtension, disabled: this.controlsDisabled }],
        faxNumber: [{ value: teamNonUserEdit.faxNumber, disabled: this.controlsDisabled }],
        taxNo: [{ value: teamNonUserEdit.taxNo, disabled: this.controlsDisabled }],
        role: [{ value: teamNonUserEdit.role, disabled: false }, Validators.required],
        subRole: [{ value: teamNonUserEdit.subRole, disabled: false }, subRoleValidator],
        createAccount: [{
          value: teamNonUserEdit.createAccount,
          disabled: !this.careTeamAccountRegistrationEnabled || teamNonUserEdit.accountCreated
        }, subRoleValidator],
        accountCreated: [teamNonUserEdit.accountCreated],
        accountRole: [{ value: teamNonUserEdit.accountRole, disabled: teamNonUserEdit.accountCreated }, accountRoleValidator],
        accountOrganization: [{
          value: teamNonUserEdit.accountOrganization,
          disabled: teamNonUserEdit.accountCreated
        }, accountOrganizationValidator],
        npi: [{ value: teamNonUserEdit.npi, disabled: false }],
        directAddress: [{ value: teamNonUserEdit.directAddress, disabled: this.controlsDisabled }],
        providerDirectorySource: [teamNonUserEdit.providerDirectorySource],
        streetLine: [{
          value: teamNonUserEdit.provider ?
            teamNonUserEdit.provider.streetLine : teamNonUserEdit.streetLine, disabled: this.controlsDisabled
        }],
        streetLineAdditional: [{
          value: teamNonUserEdit.provider ?
            teamNonUserEdit.provider.streetLineAdditional : teamNonUserEdit.streetLineAdditional, disabled: this.controlsDisabled
        }],
        city: [{ value: teamNonUserEdit.provider ? teamNonUserEdit.provider.city : teamNonUserEdit.city, disabled: this.controlsDisabled }],
        county: [{
          value: teamNonUserEdit.provider ? teamNonUserEdit.provider.county : teamNonUserEdit.county,
          disabled: this.controlsDisabled
        }],
        postalCode: [{
          value: teamNonUserEdit.provider ? teamNonUserEdit.provider.postalCode : teamNonUserEdit.postalCode,
          disabled: this.controlsDisabled
        }],
        state: [{
          value: teamNonUserEdit.provider ? teamNonUserEdit.provider.state : teamNonUserEdit.state,
          disabled: this.controlsDisabled
        }],
        country: [{
          value: teamNonUserEdit.provider ? teamNonUserEdit.provider.country : teamNonUserEdit.country,
          disabled: this.controlsDisabled
        }],
        notification: [teamNonUserEdit.notification, Validators.required],
        identifiers: this.fb.array([]),
        description: [{ value: teamNonUserEdit.description, disabled: this.controlsDisabled }],
        from: [{
          value: teamNonUserEdit.from !== null ? new Date(`${teamNonUserEdit.from}T12:00:00.000`) : null,
          disabled: false
        }],
        to: [{
          value: teamNonUserEdit.to !== null ? new Date(`${teamNonUserEdit.to}T12:00:00.000`) : null,
          disabled: false
        }],
        longitude: teamNonUserEdit.longitude,
        latitude: teamNonUserEdit.latitude,
        extensions: [{
          value: teamNonUserEdit.extensions !== undefined && teamNonUserEdit.extensions !== null ?
            teamNonUserEdit.extensions : [], disabled: false
        }],
      });
    }

    // Subscribe on role changes to handle subRole requirement
    this.editForm.get('role').valueChanges.subscribe({
      next: (role: any) =>
      {
        if (role !== undefined && role.teamType !== null)
        {
          this.subRole.setValidators([Validators.required]);
        }
        else
        {
          this.subRole.setValidators([]);
        }
        this.subRole.setValue(null);
        this.subRole.updateValueAndValidity();
      }
    });

    this.editForm.get('accountOrganization').valueChanges.subscribe({
      next: (organization: Organization) =>
      {
        if (organization !== undefined && organization !== null)
        {
          this.organization.setValue(organization.name);
          this.organization.updateValueAndValidity();
        }
      }
    });

    // Subscribe on createAccount changes to handle email requirement
    this.editForm.get('createAccount').valueChanges.subscribe({
      next: (createAccount: any) =>
      {
        if (createAccount !== undefined && createAccount)
        {
          this.accountRole.setValidators([Validators.required]);
          this.accountOrganization.setValidators([Validators.required]);
          this.email.setValidators([Validators.required, Validators.email]);
          this.emailRequired = true;

          if (this.isCommunityCoordinatorRole)
          {
            if (this.currentUserOrganization !== undefined && this.currentUserOrganization !== null)
            {
              this.organization.setValue(this.currentUserOrganization.name);
              this.accountOrganization.setValue(this.currentUserOrganization);
            }
          }
        }
        else
        {
          this.accountRole.setValidators([]);
          this.accountOrganization.setValidators([]);
          this.email.setValidators([Validators.email]);
          this.emailRequired = false;
        }
        this.accountRole.updateValueAndValidity();
        this.accountOrganization.updateValueAndValidity();
        this.email.updateValueAndValidity();
      }
    });

    // Subscribe on email changes to handle create account state when changing email address
    this.editForm.get('email').valueChanges.subscribe({
      next: (email: any) =>
      {
        if (email !== undefined && this.teamNonUserEdit !== undefined &&
          email !== this.teamNonUserEdit.email && this.teamNonUserEdit.accountCreated)
        {
          setTimeout(() =>
          {
            this.accountActivated = false;
            this.teamNonUserEdit.accountCreated = false;
            this.createAccount.enable();
            this.accountRole.enable();
            this.accountOrganization.enable();
            this.createAccount.setValue(false);
          });
        }
      }
    });

    // On edit, we must disable createAccount and accountRole if user found with the email and account not confirmed yet.
    if (teamNonUserEdit.createAccount)
    {
      this.registrationService.emailExists(teamNonUserEdit.email).subscribe({
        next: (response) =>
        {
          if (response)
          {
            teamNonUserEdit.accountCreated = true;
            this.createAccount.disable();
            this.accountRole.disable();
            this.accountOrganization.disable();
          }
        }
      });
    }

    this.setAccountActivatedState();
    this.setControlsState();
    this.teamNonUserEdit = teamNonUserEdit;

    if (this.teamNonUserEdit !== undefined && this.teamNonUserEdit.identifiers)
    {
      this.teamNonUserEdit.identifiers.forEach(data =>
      {
        this.addIdentifier(data.id, data.version, data.type, data.source, data.value, true);
      });
    }

    this.addressChanged = false;
    this.editForm.get('streetLine').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('streetLineAdditional').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('city').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('state').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('country').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('postalCode').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });

    this.editFormActive = true;
    document.body.style.overflow = 'hidden';

    this.getRoles();
    if (this.caseId !== undefined)
    {
      this.editForm.controls.case.setValue(this.caseId);
    }
    else if (this.memberId !== undefined)
    {
      this.editForm.controls.member.setValue(this.memberId);
    }
  }

  /**
   * This method is checking if the Care Team member have a registered user for the email.
   */
  private setAccountActivatedState(): void
  {
    if (this.email.value && this.email.value !== '')
    {
      this.userService.getUserByEmail(this.email.value, false).subscribe({
        next: (user) =>
        {
          this.accountActivated = user ? user.enabled : false;
          if (this.accountActivated || this.accountCreated.value)
          {
            this.email.disable();
          }
        }
      });
    }
  }

  private getRoles()
  {
    this.teamService.getTeamRoles().subscribe({
      next: (data) =>
      {
        this.teamRoles = sortBy(data, ['name']);
        this.filterTeamRoles('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private getSubRole(codeSetId: number)
  {
    if (codeSetId === null || codeSetId === undefined)
    {
      this.displaySubRoles = false;
    }
    else
    {
      this.dictionaryService.getCodesByCodeSetId(codeSetId).subscribe({
        next: (data) =>
        {
          this.subRoles = data;
          // Automatically apply selection for Channels provider
          if (this.providerDirectorySource.value === 'CHANNELS' && this.lastSelectedProviderSpecialtyCode)
          {
            setTimeout(() =>
            {
              const subRole = this.subRoles.filter(x => x.code === this.lastSelectedProviderSpecialtyCode);
              if (subRole.length === 1)
              {
                this.subRole.setValue(subRole[0]);
                this.subRole.updateValueAndValidity();
              }
            });
          }
        }
      });
      this.displaySubRoles = true;
    }
  }

  private setControlsState(): void
  {
    if (this.providerDirectorySource.value === 'CHANNELS')
    {
      this.controlsDisabled = true;
      this.identifiersVisible = false;    // Channels Providers do not have support for other identifiers yet
      this.firstName.disable();
      this.lastName.disable();
      this.middleName.disable();
      this.suffix.disable();
      this.professionalSuffix.disable();
      this.spokenLanguages.disable();
      this.gender.disable();
      this.title.disable();
      this.organization.disable();
      this.email.disable();
      this.homePhoneNumber.disable();
      this.homePhoneNumberExtension.disable();
      this.cellPhoneNumber.disable();
      this.workPhoneNumber.disable();
      this.workPhoneNumberExtension.disable();
      this.otherPhoneNumber.disable();
      this.taxNo.disable();
      this.faxNumber.disable();
      this.npi.disable();
      this.directAddress.disable();
      this.streetLine.disable();
      this.streetLineAdditional.disable();
      this.city.disable();
      this.county.disable();
      this.postalCode.disable();
      this.state.disable();
      this.country.disable();
      this.url.disable();
      this.description.disable();
    }
    else
    {
      this.controlsDisabled = false;
      this.identifiersVisible = true;
      this.firstName.enable();
      this.lastName.enable();
      this.middleName.enable();
      this.suffix.enable();
      this.professionalSuffix.enable();
      this.spokenLanguages.enable();
      this.gender.enable();
      this.title.enable();
      this.organization.enable();
      this.email.enable();
      this.homePhoneNumber.enable();
      this.homePhoneNumberExtension.enable();
      this.cellPhoneNumber.enable();
      this.workPhoneNumber.enable();
      this.workPhoneNumberExtension.enable();
      this.otherPhoneNumber.enable();
      this.taxNo.enable();
      this.faxNumber.enable();
      this.npi.enable();
      this.directAddress.enable();
      this.streetLine.enable();
      this.streetLineAdditional.enable();
      this.city.enable();
      this.county.enable();
      this.postalCode.enable();
      this.state.enable();
      this.country.enable();
      this.url.enable();
      this.description.enable();
    }
    this.firstName.updateValueAndValidity();
    this.lastName.updateValueAndValidity();
    this.middleName.updateValueAndValidity();
    this.suffix.updateValueAndValidity();
    this.professionalSuffix.updateValueAndValidity();
    this.spokenLanguages.updateValueAndValidity();
    this.gender.updateValueAndValidity();
    this.title.updateValueAndValidity();
    this.organization.updateValueAndValidity();
    this.email.updateValueAndValidity();
    this.homePhoneNumber.updateValueAndValidity();
    this.homePhoneNumberExtension.updateValueAndValidity();
    this.cellPhoneNumber.updateValueAndValidity();
    this.workPhoneNumber.updateValueAndValidity();
    this.workPhoneNumberExtension.updateValueAndValidity();
    this.otherPhoneNumber.updateValueAndValidity();
    this.taxNo.updateValueAndValidity();
    this.faxNumber.updateValueAndValidity();
    this.npi.updateValueAndValidity();
    this.directAddress.updateValueAndValidity();
    this.streetLine.updateValueAndValidity();
    this.streetLineAdditional.updateValueAndValidity();
    this.city.updateValueAndValidity();
    this.county.updateValueAndValidity();
    this.postalCode.updateValueAndValidity();
    this.state.updateValueAndValidity();
    this.country.updateValueAndValidity();
    this.url.updateValueAndValidity();
    this.description.updateValueAndValidity();
  }

  /**
   * Initialize the indentifier form group.
   *
   * @param id Identifier id
   * @param version Identifier record version
   * @param type Identifier type (dictionary)
   * @param source Identifier source (dictionary)
   * @param value Identifier value
   * @returns Identifier FormGroup object
   */
  private initIdentifier(id = 0, version = 0, type: any, source: any, value: string): UntypedFormGroup
  {
    const group: any = {};
    group.rowId = null;
    group.id = [id];
    group.type = [type, Validators.required];
    group.source = [source];
    group.value = [value, Validators.required];
    group.version = version;
    return this.fb.group(group);
  }

  /**
   * Cleaning the identifiers list object and remove temporary working variabled.
   *
   * @param objectToPersist Object to persist.
   */
  private setIdentifiers(objectToPersist: any): void
  {
    objectToPersist.identifiers.forEach(d =>
    {
      // No need to post the following properties on the server
      delete d.rowId;
      delete d.sort;
    });
  }
}
